import React, { useState, useEffect } from "react";
import { Phone, MapPin, TrendingUp } from "lucide-react";
import { useUsdToMxn } from "@/components/CurrencyConverter";
import Logo from "@/components/Logo";
import { firePhoneCallLead } from "@/lib/conversions";

export default function Footer() {
  const { rate, updatedAt } = useUsdToMxn();

  return (
    <footer className="relative bg-[#06142A] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-[68px] h-[68px] rounded-full bg-[#1E3A8A] ring-2 ring-[#F97316]/40 flex items-center justify-center p-2">
                <Logo className="w-full h-full text-white" />
              </div>
              <div className="leading-tight">
                <span className="block font-body font-extrabold text-[24.75px] leading-none tracking-[-1px] text-ochre">GBIC</span>
                <span className="block font-body font-bold text-[10.8px] leading-none tracking-[0.5px] text-white/80 mt-0.5">Servicios Aéreos&reg;</span>
              </div>
            </div>
            <p className="text-white/75 text-sm leading-relaxed tracking-wide">
              Una división de <a href="https://gbic.mx" target="_blank" rel="noopener noreferrer" className="text-ochre underline decoration-ochre/50 underline-offset-2 hover:text-[#FFA040] hover:decoration-[#FFA040] transition-colors">GBIC Comunicaciones&reg;</a>. Operaciones aéreas de precisión en México, USA y Centroamérica.
            </p>
            <p className="text-white/75 text-sm leading-relaxed tracking-wide mt-2">
              Empresa del grupo: <a href="https://gbicsp.com" target="_blank" rel="noopener noreferrer" className="text-ochre underline decoration-ochre/50 underline-offset-2 hover:text-[#FFA040] hover:decoration-[#FFA040] transition-colors">GBIC Seguridad Privada&reg;</a> — custodia y transporte de valores.
            </p>
            <div className="flex items-start gap-2 mt-3 text-white/75 text-sm">
              <MapPin className="w-4 h-4 text-ochre flex-shrink-0 mt-0.5" />
              <span className="tracking-wide leading-relaxed">Morelia, Michoacán, México</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-white text-xs tracking-[0.2em] uppercase mb-4">Contacto</h4>
            <div className="space-y-3">
              <a href="tel:4434402967" onClick={firePhoneCallLead} className="flex items-center gap-2 text-cirrus hover:text-ochre transition-colors text-sm">
                <Phone className="w-4 h-4 text-ochre" />
                <span className="font-mono tracking-wider">443 440 2967</span>
              </a>
              <a href="tel:4439800800" onClick={firePhoneCallLead} className="flex items-start gap-2 text-cirrus hover:text-ochre transition-colors text-sm">
                <Phone className="w-4 h-4 text-ochre flex-shrink-0 mt-0.5" />
                <span><span className="font-heading tracking-wider text-xs uppercase text-white/75 block mb-0.5">Oficina Central GBIC</span><span className="font-mono tracking-wider">443 980 0800</span></span>
              </a>
              <div className="flex items-start gap-2 text-white/75 text-sm">
                <MapPin className="w-4 h-4 text-ochre flex-shrink-0 mt-0.5" />
                <span>México · USA · Centroamérica</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-white text-xs tracking-[0.2em] uppercase mb-4">Servicios</h4>
            <ul className="space-y-2">
              {["Ambulancia Aérea", "Extinción de Incendios", "Logística Estratégica", "Traslado Ejecutivo", "Carga Externa", "Custodia y Transporte de Valores", "Experiencias Aéreas", "Búsqueda de Unidades Robadas", "Transporte de Combustible de Aviación"].map(s => (
                <li key={s}>
                  <a href="#servicios" className="text-white/75 text-sm hover:text-ochre transition-colors tracking-wide">{s}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Live Exchange Rate */}
          <div>
            <h4 className="font-heading font-bold text-white text-xs tracking-[0.2em] uppercase mb-4">Tipo de Cambio</h4>
            <div className="glass-panel rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-ochre" />
                <span className="text-white/75 text-xs tracking-wide">1 USD =</span>
              </div>
              {rate ? (
                <>
                  <span className="font-mono text-white text-lg sm:text-xl font-bold tracking-wider">
                    {rate.toFixed(2)} <span className="text-ochre text-sm font-normal">MXN</span>
                  </span>
                  <span className="block text-white/75 text-[10px] tracking-wider mt-1">
                    {updatedAt ? `Actualizado: ${updatedAt.toLocaleDateString("es-MX")}` : ""}
                  </span>
                </>
              ) : (
                <span className="text-white/75 text-xs">Obteniendo...</span>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/75 text-xs tracking-wider">
            © {new Date().getFullYear()} Servicios Aéreos GBIC® — GBIC Comunicaciones®. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4">
            <a href="/privacidad" className="text-white/75 text-xs hover:text-ochre transition-colors tracking-wider">Política de Privacidad</a>
            <a href="/terminos" className="text-white/75 text-xs hover:text-ochre transition-colors tracking-wider">Términos de Servicio</a>
            <a href="/admin" rel="nofollow" className="text-white/75 text-xs hover:text-ochre transition-colors tracking-wider">Admin</a>
            <a
              href="https://media.base44.com/files/public/6a5d15c6e07267225235d487/7d95a5fb3_reglamento-ley-avicion-civil-11112025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/75 text-xs hover:text-ochre transition-colors tracking-wider"
            >
              Reglamento de Aviación Civil
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}